import { Switch as UiSwitch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { Field, GroupTitle, LogoField, TextField, ToggleRow } from '@/components/common/EditorFields';

const switchCls = 'h-[26px] w-[46px] border-[3px] shrink-0';

/** The words on the poster (heading, shop details, note), the logo, the QR code and two small options. */
export default function ShopPanel({
    values, onText,
    logoSrc, onLogo,
    qr, onQr, qrUrl, onQrUrl, qrInvalid, qrPreview,
    showYear, onShowYear, wm, onWm,
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-5">
            <section className="flex flex-col gap-3">
                <GroupTitle>{t('rateChart.shop.headingGroup')}</GroupTitle>
                <Field label={t('rateChart.shop.title')}><TextField value={values.title} onChange={(v) => onText('title', v)} maxLen={40} /></Field>
                <Field label={t('rateChart.shop.subtitle')}><TextField value={values.subtitle} onChange={(v) => onText('subtitle', v)} maxLen={60} /></Field>
                <Field label={t('rateChart.shop.kicker')}><TextField value={values.kicker} onChange={(v) => onText('kicker', v)} maxLen={60} /></Field>
            </section>

            <section className="flex flex-col gap-3">
                <GroupTitle>{t('rateChart.shop.detailsGroup')}</GroupTitle>
                <Field label={t('rateChart.shop.name')}><TextField value={values.shop} onChange={(v) => onText('shop', v)} maxLen={80} /></Field>
                <Field label={t('rateChart.shop.phone')}><TextField value={values.phone} onChange={(v) => onText('phone', v)} maxLen={30} inputMode="tel" /></Field>
                <Field label={t('rateChart.shop.address')}><TextField value={values.addr} onChange={(v) => onText('addr', v)} maxLen={160} /></Field>
                <Field label={t('rateChart.shop.year')}><TextField value={values.year} onChange={(v) => onText('year', v)} maxLen={8} /></Field>
                <Field label={t('rateChart.shop.note')}><TextField multiline value={values.note} onChange={(v) => onText('note', v)} maxLen={240} /></Field>
            </section>

            <div className="h-px bg-zinc-100 dark:bg-zinc-800" />

            <LogoField value={logoSrc} onChange={onLogo} />

            <div className="flex flex-col gap-3 p-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700">
                <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t('rateChart.shop.qr')}</div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">{t('rateChart.shop.qrHint')}</div>
                    </div>
                    <UiSwitch checked={qr} onCheckedChange={onQr} className={switchCls} aria-label={t('rateChart.shop.qr')} />
                </div>
                {qr && (
                    <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0 flex flex-col gap-1">
                            <TextField value={qrUrl} onChange={onQrUrl} maxLen={300} placeholder="https://facebook.com/…" inputMode="url" className={cn(qrInvalid && '!border-red-400 focus:!ring-red-300')} />
                            {qrInvalid && <span className="text-[11.5px] text-red-600 dark:text-red-400">{t('rateChart.shop.qrInvalid')}</span>}
                        </div>
                        <div className="w-[52px] h-[52px] shrink-0 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white flex items-center justify-center">
                            {qrPreview && <img src={qrPreview} alt="" width={44} height={44} className="[image-rendering:pixelated]" />}
                        </div>
                    </div>
                )}
            </div>

            <ToggleRow title={t('rateChart.shop.yearBadge')} hint={t('rateChart.shop.yearBadgeHint')}>
                <UiSwitch checked={showYear} onCheckedChange={onShowYear} className={switchCls} aria-label={t('rateChart.shop.yearBadge')} />
            </ToggleRow>
            <ToggleRow title={t('rateChart.shop.watermark')} hint={t('rateChart.shop.watermarkHint')}>
                <UiSwitch checked={wm} onCheckedChange={onWm} className={switchCls} aria-label={t('rateChart.shop.watermark')} />
            </ToggleRow>
        </div>
    );
}

